import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import Button from '@components/ButtonTs';
import Avatar from '@components/CRM/Clients/Avatar';
import { Edit as EditIcon, SmallPlus } from '@icons/common';
import { Remove as RemoveIcon } from '@icons/crm';

import { AgentTouristDraft } from '../../api';
import { DOCUMENT_TYPE_OPTIONS } from '../constants';
import {
  B2BOrderTourist,
  B2BTouristDocumentType,
  B2BTouristGender,
} from '../types';
import TouristEditor from './TouristEditor';
import {
  createB2BOrderTouristAction,
  deleteB2BOrderTouristAction,
  updateB2BOrderTouristAction,
} from '../../../../store/actions/b2bOrders';
import { setTouristMutationError } from '../../../../store/reducers/b2bOrders';
import {
  b2bOrderTouristMutationErrorSelector,
  deletingB2BOrderTouristIdSelector,
  isB2BOrderTouristSavingSelector,
} from '../../../../store/selectors/b2bOrders';

interface TouristsBlockProps {
  orderId: string;
  tourists: B2BOrderTourist[];
}

const GENDER_COLORS: Record<B2BTouristGender, string> = {
  F: '#f48fb1',
  M: '#64b5f6',
};

const getDocumentTypeTitle = (documentType: B2BTouristDocumentType | null) =>
  DOCUMENT_TYPE_OPTIONS.find((option) => option.value === documentType)?.title;

const TouristsBlock: React.FC<TouristsBlockProps> = ({ orderId, tourists }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector(isB2BOrderTouristSavingSelector);
  const deletingTouristId = useSelector(deletingB2BOrderTouristIdSelector);
  const mutationError = useSelector(b2bOrderTouristMutationErrorSelector);
  const [editingTouristId, setEditingTouristId] = useState<
    string | 'new' | null
  >(null);
  const wasSaving = useRef(false);
  const editingTourist = tourists.find(({ id }) => id === editingTouristId);
  const isMutating = isSaving || Boolean(deletingTouristId);
  // Документ одного типа у всех туристов — тип в заголовке колонки; разные — над номером в строке.
  const documentTypes = Array.from(
    new Set(tourists.map(({ documentType }) => documentType).filter(Boolean))
  );
  const commonDocumentType = documentTypes.length === 1 ? documentTypes[0] : null;

  useEffect(() => {
    if (wasSaving.current && !isSaving && !mutationError) {
      setEditingTouristId(null);
    }

    wasSaving.current = isSaving;
  }, [isSaving, mutationError]);

  const openEditor = (touristId: string | 'new') => {
    dispatch(setTouristMutationError(null));
    setEditingTouristId(touristId);
  };

  const closeEditor = () => {
    dispatch(setTouristMutationError(null));
    setEditingTouristId(null);
  };

  const handleSave = (tourist: AgentTouristDraft) => {
    if (editingTourist) {
      dispatch(
        updateB2BOrderTouristAction({
          orderId,
          touristId: editingTourist.id,
          tourist,
        })
      );
      return;
    }

    dispatch(createB2BOrderTouristAction({ orderId, tourist }));
  };

  const handleDelete = (tourist: B2BOrderTourist) => {
    if (!window.confirm(`Удалить туриста «${tourist.fullName}» из заказа?`)) {
      return;
    }

    dispatch(
      deleteB2BOrderTouristAction({
        orderId,
        touristId: tourist.id,
      })
    );
  };

  return (
    <Block>
      <Header>
        <Title>Туристы</Title>
        <AddButton
          aria-label="Добавить туриста"
          disabled={isMutating}
          title="Добавить туриста"
          variant="functional"
          onClick={() => openEditor('new')}
        >
          <SmallPlus />
        </AddButton>
      </Header>

      {editingTouristId && (
        <TouristEditor
          key={editingTouristId}
          error={mutationError}
          isSaving={isSaving}
          tourist={editingTourist}
          onCancel={closeEditor}
          onSubmit={handleSave}
        />
      )}

      {!editingTouristId && mutationError && (
        <MutationError>{mutationError}</MutationError>
      )}

      <TableScroll>
        <Table>
        <thead>
          <tr>
            <th>Турист</th>
            <th>Дата рождения</th>
            <th>Гражданство</th>
            <th>{getDocumentTypeTitle(commonDocumentType) || 'Документ'}</th>
            <th aria-label="Действия" />
          </tr>
        </thead>
        <tbody>
          {tourists.length ? (
            tourists.map((tourist) => (
              <TouristRow key={tourist.id} $gender={tourist.gender}>
                <td>
                  <TouristName>
                    <TouristAvatar name={tourist.fullName} url={null} />
                    {tourist.fullName}
                  </TouristName>
                </td>
                <td>{tourist.birthDate}</td>
                <td>{tourist.nationalityName}</td>
                <td>
                  {!commonDocumentType && tourist.documentType && (
                    <DocumentType>
                      {getDocumentTypeTitle(tourist.documentType)}
                    </DocumentType>
                  )}
                  {tourist.passport}
                </td>
                <td>
                  <Actions>
                    <ActionButton
                      aria-label={`Редактировать ${tourist.fullName}`}
                      disabled={isMutating}
                      title="Изменить"
                      variant="functional"
                      onClick={() => openEditor(tourist.id)}
                    >
                      <EditIcon />
                    </ActionButton>
                    <DeleteButton
                      aria-label={`Удалить ${tourist.fullName}`}
                      disabled={isMutating}
                      title="Удалить"
                      variant="functional"
                      onClick={() => handleDelete(tourist)}
                    >
                      <RemoveIcon />
                    </DeleteButton>
                  </Actions>
                </td>
              </TouristRow>
            ))
          ) : (
            <tr>
              <td colSpan={5}>Туристы не добавлены</td>
            </tr>
          )}
        </tbody>
        </Table>
      </TableScroll>
    </Block>
  );
};

const Block = styled.section`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid #eceef0;
  display: flex;
  justify-content: space-between;
  padding: 8px 12px 8px 14px;
`;

const Title = styled.h3`
  font-size: 15px;
  margin: 0;
`;

const AddButton = styled(Button)`
  align-items: center;
  color: #007aff;
  display: flex;
  height: 34px;
  justify-content: center;
  width: 34px;

  svg {
    height: 22px;
    width: 22px;
  }

  path {
    stroke: currentColor;
  }

  &:hover:not(:disabled) {
    color: #179fe3;
  }

  &:disabled {
    color: #c2c5cc;
  }
`;

const MutationError = styled.div`
  background: #fff1f0;
  border-bottom: 1px solid #ffd8d5;
  color: #b42318;
  font-size: 12px;
  padding: 10px 14px;
`;

const Table = styled.table`
  border-collapse: collapse;
  text-align: left;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid #edf0f2;
    padding: 10px 14px;
  }

  /* Промежутки между колонками с данными пошире; колонке кнопок хватает её ширины. */
  th + th:not(:last-child),
  td + td:not(:last-child) {
    padding-left: 18px;
  }

  th {
    color: #919499;
    font-size: 11px;
    font-weight: 500;
  }

  td {
    font-size: 12px;
  }

  th:last-child,
  td:last-child {
    padding-left: 4px;
    padding-right: 10px;
    text-align: right;
    width: 64px;
  }

  td[colspan] {
    color: #919499;
    padding: 10px 14px;
    text-align: center;
    width: auto;
  }
`;

// На узком экране таблица прокручивается внутри блока, а не вылезает на соседнюю колонку.
const TableScroll = styled.div`
  overflow-x: auto;
`;

// Полоса слева показывает пол туриста. Тень, а не border: ячейка не сдвигается относительно заголовка.
const TouristRow = styled.tr<{ $gender: B2BTouristGender }>`
  td:first-child {
    box-shadow: inset 3px 0 0 ${({ $gender }) => GENDER_COLORS[$gender]};
  }
`;

const TouristName = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const TouristAvatar = styled(Avatar)`
  flex-shrink: 0;
  height: 28px;
  width: 28px;
`;

const DocumentType = styled.div`
  color: #919499;
  font-size: 11px;
  margin-bottom: 2px;
`;

// Кнопки видны при наведении на строку туриста и при фокусе с клавиатуры; на тач-экранах наведения нет — там всегда.
const Actions = styled.div`
  display: flex;
  gap: 2px;
  justify-content: flex-end;
  opacity: 0;
  transition: opacity 0.15s;

  ${TouristRow}:hover &,
  ${TouristRow}:focus-within & {
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 1;
  }
`;

// Иконки из разных наборов: одна толщина линий, чтобы карандаш и корзина смотрелись парой.
const ActionButton = styled(Button)`
  align-items: center;
  color: #007aff;
  display: flex;
  height: 24px;
  justify-content: center;
  width: 24px;

  svg {
    height: 14px;
    width: 14px;
  }

  path {
    stroke: currentColor;
    stroke-width: 1.2px;
    vector-effect: non-scaling-stroke;
  }

  &:disabled {
    color: #a9adb2;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #c23838;

  svg {
    height: 18px;
    width: 18px;
  }
`;

export default TouristsBlock;
